// Five GBP offers per business type (GBP Offer Library).
// An entry is either the offer title or the full offer: title, description and source note.
export type GbpOffer = { title: string; description?: string; usedBy?: string };
type OfferEntry = string | GbpOffer;

export const GBP_OFFERS: Record<string, OfferEntry[]> = {
  'Appliance Repair': [
    '$25 Off Your First Appliance Repair',
    'Free Service Call With Completed Repair',
    'Free Maintenance Check on a Second Appliance During Your Repair Visit',
    'Free Dryer Vent Cleaning With Any Dryer Repair',
    '10% Off New Appliance Installation'
  ],
  'Artificial Turf Installation': [
    'Free Artificial Turf Installation Estimate',
    '$500 Off Turf Installations Over 1,000 Sq. Ft.',
    'Free Pet-Friendly Turf Upgrade',
    '$250 Off Backyard Putting Green Installation',
    'Free Turf Cleaning and Grooming Visit With New Installation'
  ],
  'Auto Repair': [
    {
      title: '$20 OFF Any Oil Change',
      description:
        'Keep your engine running smoothly and save $20 on your next oil change.',
      usedBy: 'Used by Mavis and Brakes Plus'
    },
    {
      title: 'FREE Brake Inspection + $50 OFF Brake Repairs',
      description:
        'Squeaking or grinding brakes? Get a free brake inspection and save $50 on qualifying brake repairs.',
      usedBy: 'Similar offers used by Firestone and independent shops'
    },
    {
      title: '$50 OFF Any Repair Over $300',
      description:
        'Get the repairs your vehicle needs and save $50 on any qualifying service of $300 or more.',
      usedBy: 'Tiered repair discounts used by independent repair shops'
    },
    {
      title: 'FREE Check Engine Light Diagnostic With Repair',
      description:
        'Check engine light on? Get the diagnostic fee waived when you complete the recommended repair with us.',
      usedBy: 'Based on diagnostic-credit marketing offers'
    },
    {
      title: '15% OFF Your First Visit',
      description:
        'New to our shop? Enjoy 15% off qualifying services on your first visit.',
      usedBy: 'First-visit discounts used by independent repair shops'
    }
  ],
  'Basement Waterproofing': [
    'Free Basement Waterproofing Inspection',
    '$500 Off Complete Waterproofing Systems',
    '$100 Off Basement Window Well Installation',
    '$250 Off Sump Pump Installation or Replacement',
    'Free Dehumidifier With Complete Waterproofing System'
  ],
  'Bathroom Remodeling': [
    '$1,000 Off Your Bathroom Remodel',
    'Free Design Consultation',
    'Free Shower Door Upgrade With Full Remodel',
    '$500 Off Tub-to-Shower Conversion',
    'Free Grab Bar Installation With Walk-In Shower Remodel'
  ],
  'Cabinet Installation & Refinishing': [
    '15% Off Cabinet Refinishing',
    'Free Cabinet Design Consultation',
    '$500 Off Complete Kitchen Cabinet Installation',
    'Free Soft-Close Hinge Upgrade With Cabinet Refinishing',
    '10% Off Cabinet Repair and Door Replacement'
  ],
  'Carpet Cleaning': [
    '3 Rooms Cleaned for $99',
    'Free Hallway Cleaning With 3 Rooms',
    '$50 Off Tile and Grout Cleaning',
    '$25 Off Upholstery or Sofa Cleaning',
    'Free Pet Odor Treatment With 3-Room Cleaning'
  ],
  'Chimney Cleaning & Repair': [
    '$25 Off Chimney Sweeping',
    'Free Chimney Inspection With Cleaning',
    '$100 Off Chimney Repairs Over $500',
    '$100 Off Chimney Cap Installation',
    '$50 Off Dryer Vent Cleaning With Chimney Sweep'
  ],
  'Concrete Contractor': [
    '$500 Off New Concrete Driveways',
    'Free Concrete Installation Estimate',
    '10% Off Patio Installation',
    '$250 Off Driveway Crack Repair and Resurfacing',
    'Free Concrete Sealing With New Driveway or Patio'
  ],
  'Deck & Patio Contractor': [
    '$500 Off New Deck Installation',
    'Free Deck Design Consultation',
    '10% Off Patio Construction',
    '$300 Off Deck Repair or Refinishing',
    'Free Deck Lighting Upgrade With New Deck Installation'
  ],
  'Door Installation & Repair': [
    '$100 Off New Entry Door Installation',
    'Free Door Installation Estimate',
    '$50 Off Sliding Door Repairs',
    'Free Smart Lock Upgrade With New Entry Door Installation',
    '$49 Door Draft-Seal and Weatherstripping Service'
  ],
  'Drain Cleaning': [
    '$79 Main Drain Cleaning Special',
    'Free Camera Inspection With Qualifying Drain Service',
    '$50 Off Hydro Jetting',
    '$100 Off Sewer Line Repair Over $1,500',
    'Free Second Drain Cleared With Main Line Cleaning'
  ],
  Electrician: [
    '$50 Off Your First Electrical Service',
    'Free Electrical Safety Inspection With Panel Upgrade',
    '$100 Off Electrical Panel Replacement',
    '$100 Off EV Charger Installation',
    '$75 Off Whole-Home Surge Protector Installation'
  ],
  'Fencing Contractor': [
    '$250 Off New Fence Installation',
    'Free Fence Installation Estimate',
    'Free Gate Upgrade With Qualifying Fence Installation',
    '$150 Off Fence Repairs Over $750',
    'Free Fence Staining With New Wood Fence Installation'
  ],
  'Flooring Contractor': [
    '$500 Off Whole-Home Flooring Installation',
    'Free In-Home Flooring Estimate',
    '$99 Floor Repair Visit (Squeaks, Loose Boards, Damaged Planks)',
    '$300 Off Hardwood Floor Refinishing',
    'Free Furniture Moving With Flooring Installation'
  ],
  'Foundation Repair': [
    'Free Foundation Inspection',
    '$500 Off Qualifying Foundation Repairs',
    '10% Off Yard Drainage Correction With Foundation Repair',
    '$250 Off Crawl Space Encapsulation',
    'Free Follow-Up Foundation Check One Year After Repair'
  ],
  'Garage Door Repair & Installation': [
    '$49 Garage Door Tune-Up Special',
    '$50 Off Garage Door Spring Replacement',
    '$200 Off New Garage Door Installation',
    '$75 Off New Garage Door Opener Installation',
    'Free Weather Seal Replacement With New Garage Door'
  ],
  'General Contractor': [
    '$1,000 Off Home Renovation Projects Over $15,000',
    'Free Project Consultation',
    'Free Punch-List Visit 30 Days After Project Completion',
    '$500 Off Home Addition Projects',
    '$300 Off Accessibility Upgrades (Ramps, Wider Doorways)'
  ],
  'Gutter Installation & Cleaning': [
    '$99 Gutter Cleaning Special',
    '$200 Off Seamless Gutter Installation',
    'Free Downspout Cleaning With Full Gutter Service',
    '$150 Off Gutter Guard Installation',
    '$50 Off Gutter Repair and Re-Sealing'
  ],
  Handyman: [
    '$50 Off Your First Handyman Service',
    '10% Off Half-Day Handyman Booking',
    'Book 3 Repairs and Save $75',
    '$25 Off TV Mounting',
    'Free Smoke Detector Check With Any Handyman Visit'
  ],
  'Home Cleaning': [
    '$50 Off Your First House Cleaning',
    '$40 Off Move-In or Move-Out Cleaning',
    'Free Refrigerator Cleaning With Whole-Home Deep Clean',
    '$25 Off Every 4th Recurring Cleaning',
    'Free Baseboard and Window Sill Detailing on Your First Recurring Clean'
  ],
  'Home Inspection': [
    '$50 Off Your Home Inspection',
    'Free Thermal Imaging With Full Home Inspection',
    '$100 Off Combined Home and Wind Mitigation Inspections',
    'Free Repair Re-Check Visit After Your Home Inspection',
    '$75 Off New Construction Phase Inspections'
  ],
  'Home Remodeling': [
    '$1,000 Off Your Next Home Remodel',
    'Free In-Home Design Consultation',
    'Free Design Upgrade With Qualifying Remodel',
    '$500 Off Basement Finishing',
    'Free Dumpster and Debris Haul-Away With Qualifying Remodel'
  ],
  'Home Security Installation': [
    'Free Security System Installation With Qualifying Package',
    'Free Video Doorbell With Complete Security System',
    'Free Security Camera Placement Assessment',
    'First Month of Professional Monitoring Free',
    '$50 Off Smart Lock Installation'
  ],
  'HVAC (Heating & Air Conditioning)': [
    '$49 AC Tune-Up Special',
    '$500 Off New HVAC System Installation',
    'Free Second Opinion on HVAC Replacement',
    '$75 Off Air Duct Cleaning',
    'Free Smart Thermostat With New HVAC System Installation'
  ],
  'Insulation Contractor': [
    '$300 Off Attic Insulation Installation',
    'Free Home Insulation Assessment',
    '$100 Off Old Insulation Removal and Cleanup',
    'Free Air Sealing With Attic Insulation',
    '$150 Off Crawl Space Insulation'
  ],
  'Irrigation & Sprinkler Services': [
    '$49 Sprinkler System Inspection',
    '$100 Off Sprinkler System Repairs Over $500',
    'Free Smart Irrigation Controller With New System Installation',
    '$49 Fall Sprinkler Winterization',
    '$150 Off Drip Irrigation Installation for Garden Beds'
  ],
  'Junk Removal': [
    '$50 Off Full-Truck Junk Removal',
    '20% Off Garage Cleanouts',
    'Free Mattress Removal With Qualifying Full-Truck Pickup',
    'Free Donation Drop-Off of Usable Items With Any Pickup',
    '$75 Off Hot Tub or Shed Demolition and Removal'
  ],
  'Kitchen Remodeling': [
    '$1,500 Off Complete Kitchen Remodeling',
    'Free Kitchen Design Consultation',
    'Free Kitchen Backsplash Upgrade With Qualifying Remodel',
    '$500 Off Countertop Replacement',
    'Free Under-Cabinet Lighting With Qualifying Remodel'
  ],
  Landscaping: [
    '$200 Off Your First Landscaping Project',
    'Free Landscape Design Consultation With Installation',
    'Free Mulch Top-Off With New Plant Installation',
    '$100 Off Spring or Fall Yard Cleanup',
    '$250 Off Landscape Lighting Installation'
  ],
  'Lawn Care': [
    'First Lawn Mowing for $29',
    'Every 10th Mow Free on Recurring Service',
    'Free Lawn Fertilization With Seasonal Maintenance Package',
    '$50 Off Aeration and Overseeding',
    '$35 Off Leaf Removal Service'
  ],
  Locksmith: [
    '$20 Off Your First Locksmith Service',
    '$25 Off Home Lock Rekeying',
    'Free Additional Key With Qualifying Lock Replacement',
    '$25 Off Smart Lock Installation',
    'Free Home Security Check With Whole-Home Rekey'
  ],
  'Masonry Contractor': [
    '$250 Off Brick or Stone Installation',
    'Free Masonry Repair Estimate',
    'Free Stone Sealing With Any Masonry Repair',
    '$150 Off Chimney Repointing or Repair',
    '$99 Brick or Stone Mailbox Repair'
  ],
  'Moving Company': [
    '$100 Off Your Local Move',
    'Free Moving Boxes With Qualifying Move',
    'Free Storage for 30 Days With Qualifying Move',
    '10% Off Packing Services',
    'Free Furniture Disassembly and Reassembly With Local Move'
  ],
  'Painting Contractor': [
    '$500 Off Whole-Home Exterior Painting',
    'Free Painting Estimate',
    '15% Off Interior Painting of 3+ Rooms',
    '$200 Off Cabinet Painting',
    'Free Touch-Up Visit 6 Months After Your Project'
  ],
  'Paver Installation': [
    '$500 Off New Paver Patio Installation',
    'Free Paver Design Consultation',
    '10% Off Driveway Paver Installation',
    '$200 Off Paver Cleaning and Sealing',
    'Free Border Upgrade With New Paver Patio'
  ],
  'Pest Control': [
    '$50 Off Your First Pest Control Treatment',
    'First Month Free With Annual Pest Control Plan',
    'Free Termite Inspection',
    '$50 Off Mosquito Treatment',
    '$75 Off Rodent Exclusion Service'
  ],
  Plumber: [
    '$50 Off Any Plumbing Repair',
    '$99 Drain Cleaning Special',
    '$200 Off Water Heater Installation',
    '$75 Off Toilet Replacement',
    'Free Water Pressure Check With Any Service Call'
  ],
  'Pool Cleaning & Maintenance': [
    'First Month of Pool Cleaning for $49',
    'Free Pool Water Analysis and Chemical Balancing With First Service',
    'Free Filter Cleaning With 3 Months of Service',
    '$50 Off Green-to-Clean Pool Recovery',
    '$75 Off Pool Pump or Filter Repair'
  ],
  'Pool Construction & Remodeling': [
    '$1,000 Off New Pool Construction',
    'Free 3D Pool Design Consultation',
    'Free LED Pool Lighting Upgrade With Qualifying Pool Build',
    '$500 Off Pool Resurfacing or Remodel',
    'Free Water Feature Upgrade With Qualifying Pool Build'
  ],
  'Pressure Washing': [
    '$99 Driveway Pressure Washing Special',
    '20% Off Whole-House Pressure Washing',
    'Free Sidewalk Cleaning With Driveway and Patio Cleaning',
    '$50 Off Roof Soft Washing',
    'Free Trash Can Cleaning With Any Service'
  ],
  'Roofing Contractor': [
    '$500 Off Complete Roof Replacement',
    'Free Roof Inspection',
    '$250 Off Roof Repairs Over $1,000',
    'Free Gutter Cleaning With Roof Replacement',
    '$300 Off Roof Coating or Sealing'
  ],
  'Septic Tank Services': [
    '$50 Off Septic Tank Pumping',
    'Free Septic System Inspection With Pumping',
    '$100 Off Septic System Repairs',
    '$75 Off Septic Riser Installation',
    '10% Off With Annual Septic Maintenance Plan'
  ],
  'Siding Contractor': [
    '$1,000 Off Whole-Home Siding Replacement',
    'Free Siding Installation Estimate',
    'Free Trim and Soffit Upgrade With Whole-Home Siding Replacement',
    '$250 Off Siding Repairs Over $1,000',
    '$200 Off Gutter Replacement With New Siding'
  ],
  'Solar Panel Installation': [
    '$1,000 Off New Solar Installation',
    'Free Home Solar Savings Assessment',
    '$500 Off Battery Storage Added to a New Solar System',
    '$99 Solar Panel Cleaning Special',
    'Free Critter Guard Installation With New Solar System'
  ],
  'Tree Services': [
    '$100 Off Tree Removal',
    'Free Tree Health Assessment With Qualifying Service',
    '20% Off Stump Grinding With Tree Removal',
    '$50 Off Tree Trimming',
    'Free Wood Chip Mulch With Tree Removal'
  ],
  'Water Damage Restoration': [
    'Free Water Damage Inspection',
    'Free Odor Removal Treatment With Completed Restoration',
    '$250 Off Self-Pay Restoration Projects Over $2,500',
    '$200 Off Mold Remediation',
    '$150 Off Sewage Backup Cleanup'
  ],
  'Water Filtration & Treatment': [
    '$200 Off Whole-Home Water Filtration Installation',
    'Free Home Water Quality Test',
    'Free Filter Upgrade With New Water Softener Installation',
    '$100 Off Reverse Osmosis Drinking Water System',
    '$25 Off Annual Filter Replacement Service'
  ],
  'Waterproofing Contractor': [
    '$500 Off Home Waterproofing Projects',
    'Free Waterproofing Inspection',
    'Free Downspout Extensions With Any Waterproofing Project',
    '$250 Off Crawl Space Waterproofing',
    'Free Basement Dehumidifier With Qualifying Project'
  ],
  'Window Cleaning': [
    '20% Off Your First Window Cleaning',
    'Free Screen Cleaning With Whole-Home Window Service',
    '$50 Off Solar Panel Cleaning With Window Cleaning',
    '15% Off Recurring Quarterly Window Cleaning',
    '$40 Off Post-Construction Window Cleaning'
  ],
  'Window Installation & Replacement': [
    '$500 Off 5 or More Replacement Windows',
    'Free In-Home Window Estimate',
    '$50 Off Window Repair (Broken Seals, Foggy Glass)',
    '$100 Off Patio Door Replacement With Window Order',
    'Free Low-E Glass Upgrade With 5+ Windows'
  ]
};

const ALIASES: Record<string, string> = {
  'Waterproofing (Basement & Foundation)': 'Waterproofing Contractor'
};

const entryFor = (category: string): OfferEntry[] =>
  GBP_OFFERS[category] ?? GBP_OFFERS[ALIASES[category] ?? ''] ?? [];

const asOffer = (entry: OfferEntry): GbpOffer =>
  typeof entry === 'string' ? { title: entry } : entry;

/**
 * Editable offer text for a category. When the library has a description it is
 * part of the same text, so the whole offer (title + description) lives in one field.
 */
export const getGbpOffers = (category: string): string[] =>
  entryFor(category)
    .map(asOffer)
    .map((o) => (o.description ? `${o.title}\n\n${o.description}` : o.title));

/** Full offer copy for a category: title, description and source note. */
export const getGbpOfferDetails = (category: string): GbpOffer[] =>
  entryFor(category).map(asOffer);
